import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { AppBar, Toolbar, CircularProgress } from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";
import MarkEmailReadIcon from "@material-ui/icons/Drafts";
import { Image } from "react-bootstrap";
import { Alert } from "@material-ui/lab";
import { Link, useLocation } from "react-router-dom";

import lusukuLogo517 from "../../assets/img/lusuku_logo_main.png";
import { LOGIN_ROUTE } from "../../utilities/landingRoute";
import {
  requestPasswordResetEmail,
  resetRequestErrorMessage,
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP_URL,
  SUPPORT_WHATSAPP_DISPLAY,
} from "../../api/passwordResetApi";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Forgot password" for everyone who signs in at lusuku.shop, vendors first.
//
// Written for a shop owner on a phone: one field, one big button, and after
// sending, a screen that says exactly where to look, what the email is called,
// how long the link lasts, and what to do when it does not arrive (resend,
// fix a typo, or reach a person). The server answers the same way whether or
// not the address has an account, so this page never says "no such account";
// it asks the person to check the spelling instead.
//
// The email typed on the sign-in page is carried over, so nobody types it twice.
const AgentPasswordResetRequestScreen = () => {
  const classes = useStyles();
  const location = useLocation();
  const carried = String(location?.state?.email || "").trim();

  const [email, setEmail] = useState(carried);
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [expiresInMinutes, setExpiresInMinutes] = useState(60);
  const [cooldown, setCooldown] = useState(0);
  const [resentNote, setResentNote] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const trimmed = email.trim();
  const fieldError = !trimmed
    ? "Type the email address you use to sign in."
    : !EMAIL_SHAPE.test(trimmed)
      ? "That does not look like an email address. Check for a missing @ or dot."
      : "";

  const send = async ({ isResend = false } = {}) => {
    setTouched(true);
    setError("");
    setResentNote("");
    if (fieldError) {
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    setSending(true);
    const result = await requestPasswordResetEmail(trimmed);
    setSending(false);
    if (result.ok && result.data?.success) {
      setSentTo(trimmed);
      setExpiresInMinutes(result.data.expiresInMinutes || 60);
      setCooldown(result.data.resendAfterSeconds || 60);
      if (isResend) setResentNote("Sent again. It carries the same link, so any of the emails will work.");
      return;
    }
    setError(resetRequestErrorMessage(result));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const startOver = () => {
    setSentTo("");
    setError("");
    setResentNote("");
    setTouched(false);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const header = (
    <div className={classes.root}>
      <AppBar position="static" color="inherit" elevation={1}>
        <Toolbar>
          <Link to={LOGIN_ROUTE}>
            <Image
              width="70"
              height="75"
              className="d-inline-block align-top pb-2 mt-2"
              src={lusukuLogo517}
              alt="Lusuku"
            />
          </Link>
        </Toolbar>
      </AppBar>
    </div>
  );

  const helpLine = (
    <p className={classes.helpLine}>
      Still stuck? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or WhatsApp{" "}
      <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
        {SUPPORT_WHATSAPP_DISPLAY}
      </a>
      .
    </p>
  );

  if (sentTo) {
    const hours = expiresInMinutes % 60 === 0 ? expiresInMinutes / 60 : null;
    const validFor = hours ? `${hours} hour${hours === 1 ? "" : "s"}` : `${expiresInMinutes} minutes`;
    return (
      <div className={classes.pageWrapper}>
        {header}
        <div className={classes.contentWrapper}>
          <div className={classes.formCard} role="status" aria-live="polite">
            <div className={classes.statusIconWrapper}>
              <MarkEmailReadIcon className={classes.statusIcon} />
            </div>
            <h1 className={classes.statusTitle}>Check your email</h1>
            <p className={classes.statusSubtitle}>
              If <strong className={classes.sentTo}>{sentTo}</strong> has a Lusuku account, we have sent it a link to
              choose a new password.
            </p>

            <ol className={classes.steps}>
              <li>
                Open the email called <strong>Reset your Lusuku password</strong>.
              </li>
              <li>
                Tap <strong>Choose a new password</strong>. The link works for <strong>{validFor}</strong>.
              </li>
              <li>Pick your new password, then sign in.</li>
            </ol>

            <div className={classes.tipBox}>
              <strong>No email after 2 minutes?</strong>
              <ul>
                <li>Look in Spam, Junk or Promotions.</li>
                <li>Check the address above is spelled right.</li>
                <li>Use the email you sign in with, not a different one.</li>
              </ul>
            </div>

            {resentNote && (
              <Alert severity="success" className={classes.alert}>
                {resentNote}
              </Alert>
            )}
            {error && (
              <Alert severity="error" className={classes.alert}>
                {error}
              </Alert>
            )}

            <button
              type="button"
              className={classes.submitButton}
              onClick={() => send({ isResend: true })}
              disabled={sending || cooldown > 0}
            >
              {sending ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                  Sending...
                </>
              ) : cooldown > 0 ? (
                `Send again in ${cooldown}s`
              ) : (
                "Send the email again"
              )}
            </button>
            <button type="button" className={classes.secondaryButton} onClick={startOver}>
              Use a different email
            </button>

            <div className={classes.footer}>
              <Link to={LOGIN_ROUTE} className={classes.footerLink}>
                Back to sign in
              </Link>
              {helpLine}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showFieldError = touched && fieldError;
  return (
    <div className={classes.pageWrapper}>
      {header}
      <div className={classes.contentWrapper}>
        <div className={classes.formCard}>
          <div className={classes.iconWrapper}>
            <Image src={lusukuLogo517} alt="" className={classes.logoImage} />
          </div>
          <h1 className={classes.title}>Forgot your password?</h1>
          <p className={classes.subtitle}>
            No problem. Type the email you sign in with and we will email you a link to choose a new one.
          </p>
          <form className={classes.form} onSubmit={onSubmit} noValidate>
            <div className={classes.inputGroup}>
              <label htmlFor="reset-email" className={classes.label}>
                <EmailIcon className={classes.labelIcon} />
                Your email address
              </label>
              <input
                ref={inputRef}
                id="reset-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus={!carried}
                placeholder="name@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                onBlur={() => setTouched(true)}
                aria-invalid={Boolean(showFieldError)}
                aria-describedby={showFieldError ? "reset-email-error" : undefined}
                className={`${classes.input} ${showFieldError ? classes.inputError : ""}`}
              />
              {showFieldError && (
                <div id="reset-email-error" className={classes.errorMessage} role="alert">
                  {fieldError}
                </div>
              )}
            </div>
            {error && (
              <Alert severity="error" className={classes.alert}>
                {error}
              </Alert>
            )}
            <button type="submit" className={classes.submitButton} disabled={sending}>
              {sending ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                  Sending...
                </>
              ) : (
                "Email me a reset link"
              )}
            </button>
          </form>
          <div className={classes.footer}>
            <Link to={LOGIN_ROUTE} className={classes.footerLink}>
              I remember it. Back to sign in
            </Link>
            {helpLine}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    display: "flex",
    flexDirection: "column",
  },
  root: {
    flexGrow: 0,
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  },
  formCard: {
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)",
    padding: "48px 40px",
    maxWidth: "450px",
    width: "100%",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: "linear-gradient(90deg, #065702ff, hsla(101, 96%, 32%, 1.00))",
    },
  },
  iconWrapper: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    boxShadow: "0 3px 4px rgba(0, 0, 0, 0.4)",
    overflow: "hidden",
  },
  logoImage: {
    width: "85px",
    height: "85px",
    objectFit: "contain",
  },
  statusIconWrapper: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  },
  sentTo: {
    color: "#1a1a1a",
    wordBreak: "break-all",
  },
  steps: {
    textAlign: "left",
    fontSize: "15px",
    color: "#333",
    lineHeight: 1.6,
    paddingLeft: "22px",
    margin: "0 0 20px",
    "& li": { marginBottom: "8px" },
  },
  tipBox: {
    textAlign: "left",
    background: "#FFF8E1",
    border: "1px solid #FFE08A",
    borderRadius: "12px",
    padding: "14px 16px",
    fontSize: "14px",
    color: "#5b4a00",
    marginBottom: "20px",
    "& ul": { margin: "6px 0 0", paddingLeft: "20px" },
    "& li": { marginBottom: "2px" },
  },
  secondaryButton: {
    width: "100%",
    marginTop: "12px",
    padding: "14px 24px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#324D54",
    background: "#ffffff",
    border: "2px solid #d6dde0",
    borderRadius: "12px",
    cursor: "pointer",
    "&:hover": { borderColor: "#324D54" },
  },
  helpLine: {
    marginTop: "14px",
    marginBottom: 0,
    fontSize: "13px",
    color: "#777",
    lineHeight: 1.6,
    "& a": { color: "#324D54", fontWeight: 600 },
  },
  statusIcon: {
    fontSize: "40px",
    color: "#ffffff",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "12px",
    letterSpacing: "-0.5px",
  },
  statusTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "12px",
  },
  subtitle: {
    fontSize: "15px",
    color: "#666",
    marginBottom: "32px",
    lineHeight: 1.6,
  },
  statusSubtitle: {
    fontSize: "15px",
    color: "#666",
    marginBottom: "24px",
    lineHeight: 1.6,
  },
  form: {
    textAlign: "left",
  },
  inputGroup: {
    marginBottom: "24px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  },
  labelIcon: {
    fontSize: "18px",
    color: "#324D54",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "#fafafa",
    "&:focus": {
      borderColor: "#324D54",
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 4px rgba(50, 77, 84, 0.1)",
    },
    "&::placeholder": {
      color: "#999",
    },
  },
  inputError: {
    borderColor: "#EF4444 !important",
    "&:focus": {
      boxShadow: "0 0 0 4px rgba(239, 68, 68, 0.1) !important",
    },
  },
  errorMessage: {
    color: "#EF4444",
    fontSize: "13px",
    marginTop: "6px",
    fontWeight: 500,
  },
  submitButton: {
    minHeight: "56px",
    width: "100%",
    padding: "16px 24px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#1a1a1a",
    background: "linear-gradient(135deg, #FFC107 0%, #FFB300 100%)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 15px rgba(255, 193, 7, 0.3)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(255, 193, 7, 0.4)",
    },
    "&:disabled": {
      opacity: 0.7,
      cursor: "not-allowed",
      transform: "none",
    },
  },
  footer: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid #eee",
  },
  footerLink: {
    color: "#324D54",
    fontSize: "14px",
    fontWeight: 600,
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#1a2a2e",
      textDecoration: "none",
    },
  },
  backLink: {
    display: "inline-block",
    marginTop: "16px",
    color: "#324D54",
    fontSize: "14px",
    fontWeight: 600,
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#1a2a2e",
      textDecoration: "none",
    },
  },
  alert: {
    borderRadius: "12px",
    marginBottom: "16px",
  },
}));

export default AgentPasswordResetRequestScreen;
